import { REPORTING_TIMEZONE, formatPeriod, searchTypeLabel } from '../GscTools/period';
import { metricsFor } from '../GscTools/metrics';

/**
 * The scope every export carries in its head, so a file that outlives this page still says what
 * it is: which property, which filters, which two windows, the reporting timezone, when it was
 * fetched and whether each period came back whole.
 */
function scopeRows({ t, scope, current, baseline, fetchedAt }) {
    const coverageLabel = (period) => {
        if (!period?.coverage) return t('tools.gsc.exports.not_loaded');
        return t('tools.gsc.coverage.returned', {
            observed: period.coverage.observed.length,
            requested: period.coverage.requested.length,
            missing: period.coverage.missing.length,
        });
    };

    return [
        [t('tools.gsc.exports.property'), scope.siteUrl],
        [
            t('tools.gsc.exports.source'),
            t('tools.gsc.coverage.source', {
                type: searchTypeLabel(scope.searchType, t),
            }),
        ],
        [t('tools.gsc.controls.search_type'), scope.searchType],
        [t('tools.gsc.controls.country'), scope.country || 'all'],
        [t('tools.gsc.controls.device'), scope.device || 'all'],
        [t('tools.gsc.exports.data_state'), t(`tools.gsc.data_state.${scope.dataState}`)],
        [t('tools.gsc.coverage.current'), formatPeriod(scope.current)],
        [t('tools.gsc.coverage.baseline'), formatPeriod(scope.baseline)],
        [t('tools.gsc.exports.timezone'), REPORTING_TIMEZONE],
        [t('tools.gsc.exports.fetched_at'), fetchedAt ?? ''],
        [`${t('tools.gsc.coverage.current')} · coverage`, coverageLabel(current)],
        [`${t('tools.gsc.coverage.baseline')} · coverage`, coverageLabel(baseline)],
        [],
    ];
}

/**
 * The headline metrics and the daily series, as raw API values.
 *
 * Nothing is rounded and nothing absent is filled in: a day Search Console did not return is an
 * empty cell next to its reason, not a zero that would be summed by whoever opens the file. The
 * metric rows follow the search type, so a surface without a ranking position has no row for it;
 * the daily columns are fixed, and leave its cells empty.
 */
export function buildReportCsv({ t, scope, current, baseline, fetchedAt }) {
    const rows = scopeRows({ t, scope, current, baseline, fetchedAt });

    rows.push([
        t('tools.gsc.exports.metric'),
        t('tools.gsc.coverage.current'),
        t('tools.gsc.coverage.baseline'),
        t('tools.gsc.exports.reason'),
    ]);

    for (const name of metricsFor(scope.searchType)) {
        rows.push([
            t(`tools.gsc.metrics.${name}`),
            current?.totals?.[name] ?? null,
            baseline?.totals?.[name] ?? null,
            current?.totals?.reason ? t(`tools.gsc.reasons.${current.totals.reason}`) : null,
        ]);
    }

    rows.push([]);
    rows.push([
        t('tools.gsc.trend.day_column'),
        t('tools.gsc.trend.current_date'),
        'clicks',
        'impressions',
        'ctr',
        'position',
        t('tools.gsc.exports.state'),
        t('tools.gsc.trend.baseline_date'),
        'clicks',
        'impressions',
        'ctr',
        'position',
        t('tools.gsc.exports.state'),
    ]);

    const currentSeries = current?.series ?? [];
    const baselineSeries = baseline?.series ?? [];
    const length = Math.max(currentSeries.length, baselineSeries.length);
    const notReturned = t('tools.gsc.not_returned');
    const returned = t('tools.gsc.exports.returned');

    for (let index = 0; index < length; index += 1) {
        const currentPoint = currentSeries[index];
        const baselinePoint = baselineSeries[index];

        rows.push([
            index + 1,
            currentPoint?.date ?? null,
            currentPoint?.clicks ?? null,
            currentPoint?.impressions ?? null,
            currentPoint?.ctr ?? null,
            currentPoint?.position ?? null,
            currentPoint ? (currentPoint.returned ? returned : notReturned) : null,
            baselinePoint?.date ?? null,
            baselinePoint?.clicks ?? null,
            baselinePoint?.impressions ?? null,
            baselinePoint?.ctr ?? null,
            baselinePoint?.position ?? null,
            baselinePoint ? (baselinePoint.returned ? returned : notReturned) : null,
        ]);
    }

    return rows;
}
